import type { Location, Order } from "@/types/tms";

/**
 * Subset of order fields required to resolve a single endpoint.
 * Accepts both `orders`-shaped rows and leg rows.
 */
export type OrderEndpointSource = {
  ship_from_location_id?: string | null;
  ship_to_location_id?: string | null;
  ship_from_street_address?: string | null;
  ship_to_street_address?: string | null;
  ship_from_city?: string | null;
  ship_to_city?: string | null;
  /** Optional embedded `locations` row from a PostgREST join. When present,
   *  it is the authoritative source and bypasses list-based resolution — the
   *  short_code is returned synchronously without a separate locations fetch. */
  ship_from_location?: Location | null;
  ship_to_location?: Location | null;
};

export interface ResolvedEndpoint {
  /** The matched `locations` row, or null if nothing matched. */
  location: Location | null;
  /** `location.short_code` when matched, otherwise null. */
  shortCode: string | null;
  /** What every surface should render: short_code, else trimmed city, else "—". */
  label: string;
  /** Raw city from the order (trimmed of trailing comma/space noise). */
  city: string;
  /** Raw street_address from the order. */
  streetAddress: string;
}

const EM_DASH = "—";

function trimCity(value: string | null | undefined): string {
  return (value ?? "").replace(/[,\s]+$/, "").trim();
}

function normalize(value: string | null | undefined): string {
  return (value ?? "").trim().toLowerCase();
}

/**
 * Resolve one side of an order's route to a canonical location + label.
 *
 * Resolution order (the contract — every surface uses this and only this):
 *   1. `ship_<side>_location_id` matches a row in `locations` (authoritative FK).
 *   2. Case-insensitive match on `street_address`.
 *   3. Case-insensitive match on `city`.
 *   4. No match.
 *
 * `label` is `location.short_code` when matched, otherwise the trimmed raw city,
 * otherwise an em dash. Callers should render `label` directly without composing
 * their own fallback chain.
 */
export function resolveOrderEndpoint(
  order: OrderEndpointSource | null | undefined,
  side: "from" | "to",
  locations: Location[] | null | undefined,
): ResolvedEndpoint {
  const idKey = side === "from" ? "ship_from_location_id" : "ship_to_location_id";
  const streetKey = side === "from" ? "ship_from_street_address" : "ship_to_street_address";
  const cityKey = side === "from" ? "ship_from_city" : "ship_to_city";
  const embedKey = side === "from" ? "ship_from_location" : "ship_to_location";

  const rawCity = (order?.[cityKey] ?? "") as string;
  const rawStreet = (order?.[streetKey] ?? "") as string;
  const trimmedCity = trimCity(rawCity);

  const empty: ResolvedEndpoint = {
    location: null,
    shortCode: null,
    label: trimmedCity || EM_DASH,
    city: trimmedCity,
    streetAddress: rawStreet,
  };

  if (!order) return empty;

  // Fast path: caller embedded the `locations` row directly on the order
  // (e.g. via `ship_from_location:locations!ship_from_location_id(*)`).
  // This avoids waiting on a separate tenant-wide locations fetch.
  const embedded = (order[embedKey] ?? null) as Location | null;
  if (embedded) {
    return {
      location: embedded,
      shortCode: embedded.short_code ?? null,
      label: embedded.short_code ?? trimmedCity ?? EM_DASH,
      city: trimmedCity,
      streetAddress: rawStreet,
    };
  }

  if (!locations || locations.length === 0) return empty;

  const locationId = (order[idKey] ?? null) as string | null;
  let match: Location | undefined;

  if (locationId) {
    match = locations.find((l) => l.id === locationId);
  }

  if (!match) {
    const street = normalize(rawStreet);
    if (street) {
      match = locations.find((l) => normalize(l.street_address) === street);
    }
  }

  if (!match) {
    const city = normalize(rawCity);
    if (city) {
      match = locations.find((l) => normalize(l.city) === city);
    }
  }

  if (!match) return empty;

  return {
    location: match,
    shortCode: match.short_code ?? null,
    label: match.short_code ?? trimmedCity ?? EM_DASH,
    city: trimmedCity,
    streetAddress: rawStreet,
  };
}

/** Convenience: resolve both endpoints at once. */
export function resolveOrderEndpoints(
  order: OrderEndpointSource | null | undefined,
  locations: Location[] | null | undefined,
): { from: ResolvedEndpoint; to: ResolvedEndpoint } {
  return {
    from: resolveOrderEndpoint(order, "from", locations),
    to: resolveOrderEndpoint(order, "to", locations),
  };
}

// Re-export the Order type narrowing for callers that want a typed helper.
export type { Order, Location };